"use client";

import { useState } from "react";
import { Loader2, Pencil, Plus, Trash2 } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Checkbox } from "@/components/ui/checkbox";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { CustomerSlugInput } from "../categories/customer-slug-input";
import { AttributeValueEditor } from "@/components/catalog/categories/attribute-value-editor";
import { deriveAttributeKey } from "@/lib/categories/attribute-key";
import { customerSlugError } from "@/lib/categories/attribute-slugs";
import {
  ATTRIBUTE_TYPE_LABELS,
  enumChoicesError,
} from "@/lib/categories/attribute-definition";
import {
  completeLanguageMap,
  localizedText,
  sparseLanguageMap,
} from "@/lib/categories/localization";
import type { AttributeChoice } from "@/services/category.service";
import {
  CatalogueTemplateService,
  type CategoryTemplateAttribute,
  type CategoryTemplateAttributeValueType,
} from "@/services/catalogue-template.service";

interface CategoryTemplateAttributeEditorProps {
  templateId: string;
  categoryId: string | null;
  categoryName: string;
  languages: string[];
  attributes: CategoryTemplateAttribute[];
  readOnly: boolean;
  onChanged: () => void;
}

interface AttributeFormState {
  key: string;
  label: Record<string, string>;
  valueType: CategoryTemplateAttributeValueType;
  required: boolean;
  configuration: Record<string, unknown>;
  description: string;
  attribute_slug: Record<string, string>;
  choices: AttributeChoice[];
  sortOrder: string;
}

type AttributeDialogState =
  | { mode: "create" }
  | { mode: "edit"; attribute: CategoryTemplateAttribute }
  | null;

const emptyMap = (languages: readonly string[]) =>
  Object.fromEntries(languages.map((language) => [language, ""]));

const emptyForm = (languages: readonly string[]): AttributeFormState => ({
  key: "",
  label: emptyMap(languages),
  valueType: "string",
  required: false,
  configuration: {},
  description: "",
  attribute_slug: emptyMap(languages),
  choices: [],
  sortOrder: "0",
});

function parseSortOrder(value: string): number {
  const parsed = Number.parseInt(value, 10);
  return Number.isFinite(parsed) ? parsed : 0;
}

function configuredChoices(value: unknown): AttributeChoice[] {
  if (!Array.isArray(value)) return [];
  return value.filter((choice): choice is AttributeChoice =>
    Boolean(
      choice
      && typeof choice === "object"
      && "value" in choice
      && "label" in choice
      && choice.label
      && typeof choice.label === "object",
    ),
  ).map((choice) => ({
    ...choice,
    label: { ...choice.label },
    ...(choice.slug ? { slug: { ...choice.slug } } : {}),
  }));
}

export function CategoryTemplateAttributeEditor({
  templateId,
  categoryId,
  categoryName,
  languages,
  attributes,
  readOnly,
  onChanged,
}: CategoryTemplateAttributeEditorProps) {
  const scopedAttributes = attributes.filter((attribute) =>
    categoryId ? attribute.categoryId === categoryId : !attribute.categoryId
  );
  const [dialog, setDialog] = useState<AttributeDialogState>(null);
  const [form, setForm] = useState<AttributeFormState>(() => emptyForm(languages));
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const updateForm = (patch: Partial<AttributeFormState>) =>
    setForm((current) => ({ ...current, ...patch }));

  const openCreate = () => {
    setForm(emptyForm(languages));
    setError(null);
    setDialog({ mode: "create" });
  };

  const openEdit = (attribute: CategoryTemplateAttribute) => {
    setForm({
      key: attribute.key,
      attribute_slug: Object.fromEntries(
        languages.map((language) => [language, attribute.slug?.[language] ?? ""]),
      ),
      label: Object.fromEntries(
        languages.map((language) => [language, attribute.label[language] ?? ""]),
      ),
      valueType: attribute.valueType,
      required: attribute.required,
      configuration: { ...attribute.configuration },
      description:
        typeof attribute.configuration.description === "string"
          ? attribute.configuration.description
          : "",
      choices: configuredChoices(attribute.configuration.choices),
      sortOrder: String(attribute.sortOrder),
    });
    setError(null);
    setDialog({ mode: "edit", attribute });
  };

  const submitDialog = async () => {
    if (!dialog) return;
    const key = form.key.trim();
    if (!key || languages.some((language) => !form.label[language]?.trim())) {
      setError("Id and every language label are required");
      return;
    }
    const slugError = languages
      .map((language) => customerSlugError(form.attribute_slug[language] ?? ""))
      .find(Boolean);
    if (slugError) {
      setError(slugError);
      return;
    }
    if (form.valueType === "enum") {
      const choicesError = enumChoicesError(form.choices);
      if (choicesError) {
        setError(choicesError);
        return;
      }
    }
    if (
      form.choices.some((choice) =>
        languages.some((language) => !choice.label[language]?.trim()),
      )
    ) {
      setError("Every configured value needs a label in every template language");
      return;
    }

    const configuration: Record<string, unknown> = {
      description: form.description.trim(),
      ...(form.choices.length ? { choices: form.choices } : {}),
    };

    const slug = sparseLanguageMap(languages, form.attribute_slug);
    const payload = {
      category_id: categoryId,
      key,
      label: completeLanguageMap(languages, form.label),
      ...(Object.keys(slug).length ? { slug } : {}),
      value_type: form.valueType,
      required: form.required,
      configuration,
      sort_order: parseSortOrder(form.sortOrder),
    };

    setBusy(true);
    setError(null);
    try {
      if (dialog.mode === "create") {
        await CatalogueTemplateService.createCategoryTemplateAttribute(templateId, payload);
      } else {
        await CatalogueTemplateService.updateCategoryTemplateAttribute(
          templateId,
          dialog.attribute.id,
          payload,
        );
      }
      setDialog(null);
      onChanged();
    } catch (err) {
      setError(err instanceof Error ? err.message : "Attribute save failed");
    } finally {
      setBusy(false);
    }
  };

  const deleteAttribute = async (attribute: CategoryTemplateAttribute) => {
    if (!window.confirm(`Delete attribute "${attribute.key}" from this draft template?`)) return;
    try {
      await CatalogueTemplateService.deleteCategoryTemplateAttribute(templateId, attribute.id);
      onChanged();
    } catch (err) {
      alert(err instanceof Error ? err.message : "Attribute delete failed");
    }
  };

  return (
    <div className="space-y-3">
      <div className="flex flex-wrap items-center justify-between gap-2">
        <div>
          <h3 className="text-sm font-semibold">{categoryName}</h3>
          <p className="text-xs text-muted-foreground">
            {categoryId
              ? "Attributes attached to this category only."
              : "General defaults used when no category-specific attribute exists."}
          </p>
        </div>
        <Button type="button" variant="outline" size="sm" onClick={openCreate} disabled={readOnly}>
          <Plus className="mr-1 h-4 w-4" /> Attribute
        </Button>
      </div>

      {scopedAttributes.length === 0 ? (
        <p className="rounded-md border border-dashed p-4 text-sm text-muted-foreground">
          No attributes in this scope.
        </p>
      ) : (
        <div className="overflow-x-auto rounded-md border">
          <table className="w-full min-w-[720px] text-sm">
            <thead className="bg-muted/50 text-left text-xs text-muted-foreground">
              <tr>
                <th className="px-3 py-2 font-medium">Key</th>
                <th className="px-3 py-2 font-medium">English label</th>
                <th className="px-3 py-2 font-medium">Type</th>
                <th className="px-3 py-2 font-medium">Required</th>
                <th className="px-3 py-2 font-medium">Sort</th>
                <th className="px-3 py-2 text-right font-medium">Actions</th>
              </tr>
            </thead>
            <tbody>
              {scopedAttributes.map((attribute) => (
                <tr key={attribute.id} className="border-t">
                  <td className="px-3 py-2 font-mono text-xs">{attribute.key}</td>
                  <td className="px-3 py-2">{localizedText(attribute.label)}</td>
                  <td className="px-3 py-2 font-mono text-xs">
                    {ATTRIBUTE_TYPE_LABELS[attribute.valueType]}
                  </td>
                  <td className="px-3 py-2">{attribute.required ? "Yes" : "No"}</td>
                  <td className="px-3 py-2">{attribute.sortOrder}</td>
                  <td className="px-3 py-2">
                    <div className="flex justify-end gap-1">
                      <Button type="button" variant="ghost" size="icon" className="h-8 w-8"
                        onClick={() => openEdit(attribute)} disabled={readOnly}
                        aria-label={`Edit ${attribute.key}`}>
                        <Pencil className="h-4 w-4" />
                      </Button>
                      <Button type="button" variant="ghost" size="icon"
                        className="h-8 w-8 text-muted-foreground hover:text-red-600"
                        onClick={() => deleteAttribute(attribute)} disabled={readOnly}
                        aria-label={`Delete ${attribute.key}`}>
                        <Trash2 className="h-4 w-4" />
                      </Button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      <Dialog open={!!dialog} onOpenChange={(open) => !open && setDialog(null)}>
        <DialogContent className="max-h-[90vh] max-w-3xl overflow-y-auto">
          <DialogHeader>
            <DialogTitle>{dialog?.mode === "create" ? "Add attribute" : "Edit attribute"}</DialogTitle>
            <DialogDescription>
              Add a shopper label for every template language. The id remains language-neutral.
            </DialogDescription>
          </DialogHeader>

          <div className="grid items-start gap-4 py-2 md:grid-cols-2">
            {languages.map((language) => (
              <div key={language} className="grid gap-2">
                <Label htmlFor={`template-attribute-label-${language}`}>
                  {language.toUpperCase()} label
                </Label>
                <Input
                  id={`template-attribute-label-${language}`}
                  autoFocus={language === "en"}
                  value={form.label[language] ?? ""}
                  aria-invalid={!form.label[language]?.trim()}
                  onChange={(event) => {
                    const label = { ...form.label, [language]: event.target.value };
                    updateForm(
                      dialog?.mode === "create" && language === "en"
                        ? { label, key: deriveAttributeKey(event.target.value) }
                        : { label },
                    );
                  }}
                  placeholder="Required"
                />
              </div>
            ))}
            <div className="grid gap-2">
              <Label htmlFor="template-attribute-key">Id</Label>
              <Input id="template-attribute-key" className="font-mono text-[13px]"
                value={form.key} onChange={(event) => updateForm({ key: event.target.value })}
                placeholder="material" />
            </div>
            <div className="grid gap-2">
              <Label htmlFor="template-attribute-sort">Sort order</Label>
              <Input id="template-attribute-sort" type="number" value={form.sortOrder}
                onChange={(event) => updateForm({ sortOrder: event.target.value })} />
            </div>
            <div className="grid gap-3 md:col-span-2 sm:grid-cols-2">
              {languages.map((language) => (
                <CustomerSlugInput
                  key={language}
                  id={`template-attribute-key-slug-${language}`}
                  label={`${language.toUpperCase()} attribute slug`}
                  value={form.attribute_slug[language] ?? ""}
                  disabled={busy}
                  onChange={(value) =>
                    updateForm({
                      attribute_slug: { ...form.attribute_slug, [language]: value },
                    })
                  }
                />
              ))}
            </div>
            <div className="md:col-span-2">
              <AttributeValueEditor
                id="template-attribute"
                name={localizedText(form.label) || "Attribute"}
                valueType={form.valueType}
                choices={form.choices}
                languages={languages as any}
                disabled={busy || readOnly}
                onChange={updateForm}
              />
            </div>
            <label className="flex items-center gap-2 text-sm md:col-span-2">
              <Checkbox checked={form.required}
                onCheckedChange={(checked) => updateForm({ required: checked === true })} />
              Required attribute
            </label>
            <div className="grid gap-2 md:col-span-2">
              <Label htmlFor="template-attribute-description">What should Youzu look for?</Label>
              <Textarea id="template-attribute-description" value={form.description}
                placeholder="Describe the value to extract. For numbers, specify the unit."
                onChange={(event) => updateForm({ description: event.target.value })}
                disabled={busy || readOnly} />
            </div>
            {error ? <p className="text-sm text-red-600 md:col-span-2">{error}</p> : null}
          </div>

          <DialogFooter>
            <Button type="button" variant="outline" onClick={() => setDialog(null)} disabled={busy}>
              Cancel
            </Button>
            <Button type="button" onClick={submitDialog} disabled={busy}>
              {busy ? <Loader2 className="mr-1 h-4 w-4 animate-spin" /> : null}
              {dialog?.mode === "create" ? "Create" : "Save"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
